import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Clock3, Filter, ShieldAlert } from 'lucide-react';
import EmptyState from '@/components/common/EmptyState.jsx';
import PageHeaderCompact from '@/components/common/PageHeaderCompact.jsx';
import { getAlertasPriorizadas } from '@/services/reportesAlertas.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';

const SEVERITY_META = {
  critica: {
    label: 'Críticas',
    singular: 'Crítica',
    tone: 'border-[rgba(var(--danger),0.28)] bg-[rgba(var(--danger),0.1)] text-[rgb(var(--danger))]',
    softTone: 'bg-[rgba(var(--danger),0.08)]',
  },
  alta: {
    label: 'Altas',
    singular: 'Alta',
    tone: 'border-[rgba(var(--warning),0.3)] bg-[rgba(var(--warning),0.12)] text-[rgb(var(--fg))]',
    softTone: 'bg-[rgba(var(--warning),0.12)]',
  },
  media: {
    label: 'Medias',
    singular: 'Media',
    tone: 'border-[rgba(var(--brand),0.26)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]',
    softTone: 'bg-[rgba(var(--brand),0.08)]',
  },
  baja: {
    label: 'Bajas',
    singular: 'Baja',
    tone: 'border-[rgba(var(--muted),0.35)] bg-[rgba(var(--muted),0.14)] text-[rgb(var(--fg))]',
    softTone: 'bg-[rgba(var(--muted),0.12)]',
  },
};

const TYPE_OPTIONS = [
  { value: '', label: 'Todos los tipos' },
  { value: 'FOJA_CERRADA_NO_RECIBIDA', label: 'Foja cerrada no recibida' },
  { value: 'GUIA_RETENIDA_ANTIGUA', label: 'Guía retenida antigua' },
  { value: 'REPARTO_ABIERTO_ANTIGUO', label: 'Reparto abierto antiguo' },
  { value: 'MINUTA_ABIERTA_ANTIGUA', label: 'Minuta abierta antigua' },
  { value: 'GUIA_VALORIZADA_NO_FACTURADA', label: 'Guía valorizada no facturada' },
  { value: 'COBRANZA_NO_RENDIDA', label: 'Cobranza no rendida' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'Todas las severidades' },
  { value: 'critica', label: 'Críticas' },
  { value: 'alta', label: 'Altas' },
  { value: 'media', label: 'Medias' },
  { value: 'baja', label: 'Bajas' },
];

const createInitialFilters = (sucursalId = '') => ({
  severidad: '',
  tipo: '',
  sucursalId: sucursalId ? String(sucursalId) : '',
  desde: '',
  hasta: '',
  limit: 200,
});

const dedupeSucursales = (items = []) => {
  const map = new Map();
  items.forEach((item) => {
    const id = String(item?.id || item?.sucursal_id || '');
    if (!id || map.has(id)) return;
    map.set(id, {
      id,
      nombre: item?.nombre || item?.label || `Sucursal #${id}`,
    });
  });
  return Array.from(map.values());
};

const formatDateTime = (value) => {
  if (!value) return 'Sin fecha';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const getAlertRoute = (alert) => {
  if (alert.entidad_tipo === 'GUIA') return `/guias/${alert.entidad_id}`;
  if (alert.entidad_tipo === 'FOJA') return `/fojas/recepcion/${alert.entidad_id}`;
  if (alert.entidad_tipo === 'REPARTO') return `/repartos/${alert.entidad_id}`;
  if (alert.entidad_tipo === 'MINUTA') return `/tesoreria/minutas/${alert.entidad_id}`;
  if (alert.entidad_tipo === 'FACTURA') return `/facturacion/${alert.entidad_id}`;
  return null;
};

function SummaryCard({ severityKey, total }) {
  const meta = SEVERITY_META[severityKey];
  return (
    <article className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--panel),0.96)] p-4 shadow-[0_16px_30px_rgba(var(--shadow),0.12)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">
            {meta.label}
          </p>
          <p className="mt-2 text-3xl font-semibold text-[rgb(var(--fg))]">{total}</p>
        </div>
        <span className={`inline-flex min-h-9 items-center rounded-full border px-3 text-xs font-semibold ${meta.tone}`}>
          {meta.singular}
        </span>
      </div>
    </article>
  );
}

function SeverityBadge({ severity }) {
  const meta = SEVERITY_META[severity] || SEVERITY_META.media;
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.tone}`}>
      {meta.singular}
    </span>
  );
}

export default function AlertasOperativas() {
  const { sucursalActiva, sucursalPrincipal, sucursalesHabilitadas } = useAuth();
  const activeSucursalId = sucursalActiva?.id || sucursalActiva?.sucursal_id || sucursalPrincipal?.id || '';
  const [filters, setFilters] = useState(() => createInitialFilters(activeSucursalId));
  const [data, setData] = useState({
    criticas: [],
    altas: [],
    medias: [],
    bajas: [],
    resumen: { criticas: 0, altas: 0, medias: 0, bajas: 0, total: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const sucursalOptions = useMemo(
    () => dedupeSucursales([sucursalPrincipal, ...(Array.isArray(sucursalesHabilitadas) ? sucursalesHabilitadas : [])]),
    [sucursalPrincipal, sucursalesHabilitadas],
  );

  useEffect(() => {
    setFilters((current) => (current.sucursalId ? current : { ...current, sucursalId: activeSucursalId ? String(activeSucursalId) : '' }));
  }, [activeSucursalId]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const payload = await getAlertasPriorizadas(filters);
        if (!active) return;
        setData(payload || {
          criticas: [],
          altas: [],
          medias: [],
          bajas: [],
          resumen: { criticas: 0, altas: 0, medias: 0, bajas: 0, total: 0 },
        });
      } catch (loadError) {
        if (!active) return;
        setError(loadError?.response?.data?.message || 'No se pudieron cargar las alertas operativas priorizadas.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [filters]);

  const rows = useMemo(
    () => [...(data.criticas || []), ...(data.altas || []), ...(data.medias || []), ...(data.bajas || [])],
    [data],
  );

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,rgba(var(--bg),1)_0%,rgba(var(--panel),0.92)_100%)]">
      <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-4 px-4 py-4 md:px-6">
        <PageHeaderCompact
          title="Alertas operativas priorizadas"
          description="Casos accionables detectados sobre datos persistidos existentes, acotados a la sucursal operativa vigente."
          action={(
            <button
              type="button"
              onClick={() => setFilters((current) => ({ ...current }))}
              className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <Clock3 className="h-4 w-4" />
              Actualizar
            </button>
          )}
        />

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard severityKey="critica" total={data.resumen?.criticas || 0} />
          <SummaryCard severityKey="alta" total={data.resumen?.altas || 0} />
          <SummaryCard severityKey="media" total={data.resumen?.medias || 0} />
          <SummaryCard severityKey="baja" total={data.resumen?.bajas || 0} />
        </section>

        <section className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--panel),0.96)] p-4 shadow-[0_16px_30px_rgba(var(--shadow),0.12)]">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[rgb(var(--fg))]">
            <Filter className="h-4 w-4" />
            Filtros
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-[0.14em] text-[rgba(var(--muted),0.95)]">
              Severidad
              <select
                value={filters.severidad}
                onChange={(event) => setFilters((current) => ({ ...current, severidad: event.target.value }))}
                className="rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.92)] px-3 py-2 text-sm font-normal text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              >
                {SEVERITY_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-[0.14em] text-[rgba(var(--muted),0.95)]">
              Tipo
              <select
                value={filters.tipo}
                onChange={(event) => setFilters((current) => ({ ...current, tipo: event.target.value }))}
                className="rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.92)] px-3 py-2 text-sm font-normal text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              >
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-[0.14em] text-[rgba(var(--muted),0.95)]">
              Sucursal
              <select
                value={filters.sucursalId}
                onChange={(event) => setFilters((current) => ({ ...current, sucursalId: event.target.value }))}
                className="rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.92)] px-3 py-2 text-sm font-normal text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              >
                {sucursalOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.nombre}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-[0.14em] text-[rgba(var(--muted),0.95)]">
              Desde
              <input
                type="date"
                value={filters.desde}
                onChange={(event) => setFilters((current) => ({ ...current, desde: event.target.value }))}
                className="rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.92)] px-3 py-2 text-sm font-normal text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium uppercase tracking-[0.14em] text-[rgba(var(--muted),0.95)]">
              Hasta
              <input
                type="date"
                value={filters.hasta}
                onChange={(event) => setFilters((current) => ({ ...current, hasta: event.target.value }))}
                className="rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.92)] px-3 py-2 text-sm font-normal text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              />
            </label>
          </div>
        </section>

        {loading ? (
          <section className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--panel),0.96)] p-5 text-sm text-[rgb(var(--muted))] shadow-[0_16px_30px_rgba(var(--shadow),0.12)]">
            Cargando alertas operativas priorizadas...
          </section>
        ) : null}

        {error ? (
          <section className="rounded-2xl border border-[rgba(var(--danger),0.28)] bg-[rgba(var(--danger),0.1)] p-4 text-sm text-[rgb(var(--danger))] shadow-[0_16px_30px_rgba(var(--shadow),0.12)]">
            {error}
          </section>
        ) : null}

        {!loading && !error && rows.length === 0 ? (
          <EmptyState
            icon={ShieldAlert}
            title="Sin alertas para estos filtros"
            description="No hay casos vencidos para la combinación seleccionada. Ajustá rango, tipo o severidad si necesitás otra vista."
          />
        ) : null}

        {!loading && !error && rows.length > 0 ? (
          <section className="overflow-hidden rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--panel),0.98)] shadow-[0_18px_34px_rgba(var(--shadow),0.14)]">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-[rgb(var(--fg))]">
                <thead className="bg-[rgba(var(--subpanel),0.8)] text-[11px] uppercase tracking-[0.14em] text-[rgba(var(--muted),0.95)]">
                  <tr>
                    <th className="px-3 py-3 text-left">Severidad</th>
                    <th className="px-3 py-3 text-left">Alerta</th>
                    <th className="px-3 py-3 text-left">Entidad</th>
                    <th className="px-3 py-3 text-left">Cliente</th>
                    <th className="px-3 py-3 text-left">Estado</th>
                    <th className="px-3 py-3 text-left">Origen</th>
                    <th className="px-3 py-3 text-right">Antigüedad</th>
                    <th className="px-3 py-3 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((alert) => {
                    const actionRoute = getAlertRoute(alert);
                    const severityMeta = SEVERITY_META[alert.severidad] || SEVERITY_META.media;
                    return (
                      <tr key={alert.id} className="border-t border-[rgba(var(--border),0.45)] align-top">
                        <td className="px-3 py-3">
                          <SeverityBadge severity={alert.severidad} />
                        </td>
                        <td className="px-3 py-3">
                          <div className="min-w-[280px]">
                            <p className="font-semibold text-[rgb(var(--fg))]">{alert.titulo}</p>
                            <p className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${severityMeta.softTone}`}>
                              {alert.tipo}
                            </p>
                            <p className="mt-2 text-[13px] leading-5 text-[rgba(var(--muted),0.92)]">{alert.descripcion}</p>
                            <p className="mt-2 text-[12px] text-[rgba(var(--fg),0.8)]">
                              Sugerencia: {alert.accion_sugerida}
                            </p>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-[13px]">
                          <div className="font-medium">{alert.entidad_tipo}</div>
                          <div className="mt-1 text-[rgba(var(--muted),0.9)]">
                            {alert.numero_referencia ? `#${alert.numero_referencia}` : alert.entidad_id ? `#${alert.entidad_id}` : 'Sin referencia'}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-[13px] text-[rgba(var(--fg),0.88)]">{alert.cliente || 'Sin cliente'}</td>
                        <td className="px-3 py-3 text-[13px] text-[rgba(var(--fg),0.88)]">{alert.estado_actual || 'Sin estado'}</td>
                        <td className="px-3 py-3 text-[13px] text-[rgba(var(--fg),0.88)]">{formatDateTime(alert.fecha_origen)}</td>
                        <td className="px-3 py-3 text-right text-[13px] font-semibold">{alert.antiguedad_horas}h</td>
                        <td className="px-3 py-3 text-right">
                          {actionRoute ? (
                            <Link
                              to={actionRoute}
                              className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                            >
                              Ver
                              <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                          ) : (
                            <span className="inline-flex items-center rounded-xl border border-[rgba(var(--border),0.55)] px-3 py-2 text-xs text-[rgba(var(--muted),0.95)]">
                              Sin ruta
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        {!loading && !error ? (
          <div className="flex items-center gap-2 rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.9)] px-4 py-3 text-sm text-[rgba(var(--muted),0.95)]">
            <AlertTriangle className="h-4 w-4 text-[rgb(var(--brand))]" />
            Total visible: <strong className="text-[rgb(var(--fg))]">{data.resumen?.total || 0}</strong>
          </div>
        ) : null}
      </div>
    </div>
  );
}
